import React, { FC, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

// Custom
import AppPage from '@/ui/AppPage/AppPage';
import Permissions from '@/lib/permissions';
import LoginLogsChart, { ChartType } from './LoginLogsChart';
import LoginLogsTable from './LoginLogsTable';
import TenantContext from '@/context/TenantContext/TenantContext';

// Types
import { adminService, GraphData } from '@/services/adminService';

const LoginLogsPage: FC = () => {
  const tenantContext = useContext(TenantContext);
  const [graphData, setGraphData] = useState<GraphData | null>();
  const [chart, setChart] = useState<ChartType>({
    options: {
      chart: {
        id: "not-loaded"
      },
      xaxis: {
        categories: []
      },
      colors: ["#b7c800"]
    },
    series: [
      {
        name: "n-a",
        data: [],
      }
    ]
  });
  const [chartPerUser, setChartPerUser] = useState<ChartType>({
    options: {
      chart: {
        id: "not-loaded"
      },
      xaxis: {
        categories: []
      },
      colors: ["#b7c800"]
    },
    series: [
      {
        name: "n-a",
        data: [],
      }
    ]
  });
  const [showGraphs, setShowGraphs] = useState(false);
  const { t } = useTranslation();

  const handleSelectTenant = (tenantId: number) => {
    adminService.getLoginStats(tenantId)
      .then((resp) => {
        if (resp.success && resp.data) {
          const data = resp.data as GraphData;
          setGraphData(data);
          setShowGraphs(true)
        } else {
          setGraphData(null);
          setShowGraphs(false);
        }
      })
  }

  useEffect(() => {
    if (graphData) {
      const xAxis: string[] = []
      const yAxis: number[] = []
      const yAxisPar: number[] = []
      for (let i = 1; i <= 31; i += 1) {
        xAxis.push(i.toString());
        const currentInd = graphData?.stat_current_month?.find((value) => value.day === i.toString())
        const previousInd = graphData?.stat_previous_month?.find((value) => value.day === i.toString())
        if (currentInd) yAxis.push(currentInd.count)
        else yAxis.push(0)
        if (previousInd) yAxisPar.push(previousInd.count)
        else yAxisPar.push(0)
      }
      setChart({
        options: {
          chart: {
            id: t("admin:login_logs.stats_month")
          },
          xaxis: {
            categories: xAxis
          },
          colors: [tenantContext?.tenantTheme?.primary_main || '#b7c800', tenantContext?.tenantTheme?.secondary_main || '#2a80b8'],
        },
        series: [
          {
            name: `${t(`admin:login_logs.month_${graphData.current_time.current_month % 12}`)  } ${  graphData.current_time.current_year}`,
            data: yAxis,
          },
          {
            name: `${t(`admin:login_logs.month_${(graphData.current_time.current_month - 1) % 12}`)  } ${  graphData.current_time.current_year}`,
            data: yAxisPar,
          },
        ],
      })
      const xAxis2: string[] = []
      const yAxis2 =  new Map()
      const yAxisPar2 = new Map()
      graphData?.stat_current_month_per_user?.forEach((value) => { xAxis2.push(value.username); yAxis2.set(value.username, 0); yAxisPar2.set(value.username, 0) })
      graphData?.stat_previous_month_per_user?.forEach((value) => { if (!xAxis2.includes(value.username)) xAxis2.push(value.username) })
      graphData?.stat_current_month_per_user?.forEach((value) => { yAxis2.set(value.username, value.count) })
      graphData?.stat_previous_month_per_user?.forEach((value) => { yAxisPar2.set(value.username, value.count) })
      setChartPerUser({
        options: {
          chart: {
            id: t("admin:login_logs.stats_month_per_user")
          },
          xaxis: {
            categories: xAxis2
          },
          colors: [tenantContext?.tenantTheme?.primary_main || '#b7c800', tenantContext?.tenantTheme?.secondary_main || '#2a80b8'],
        },
        series: [
          {
            name: `${t(`admin:login_logs.month_${graphData.current_time.current_month % 12}`)  } ${  graphData.current_time.current_year}`,
            data: Array.from(yAxis2.values()),
          },
          {
            name: `${t(`admin:login_logs.month_${(graphData.current_time.current_month - 1) % 12}`)  } ${  graphData.current_time.current_year}`,
            data: Array.from(yAxisPar2.values()),
          }
        ]
      })
    }

  }, [graphData])

  return (
    <AppPage
      titlett="admin:page.login_logs"
      needPermission={[Permissions.Administration]}
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box m={0} style={{ height: '100%' }}>
            <LoginLogsTable onSelectRecord={handleSelectTenant} />
          </Box>
        </Grid>
        <Grid size={12}>
          <Box display="flex" alignItems="center" justifyContent="center" gap='6rem'>
            {showGraphs ? (<LoginLogsChart chart={chart} />
            ) : (<p>{graphData && t('admin:login_logs.unavailable')}</p>)}
          </Box>
        </Grid>
        <Grid size={12}>
          <Box display="flex" alignItems="center" justifyContent="center" gap='6rem'>
            {showGraphs ? (
              <LoginLogsChart chart={chartPerUser} />
            ) : null}
          </Box>
        </Grid>

      </Grid>
    </AppPage>
  );
}

export default LoginLogsPage;

